import { FileText, Lightbulb, ListChecks, NotebookPen } from 'lucide-react';
import { TutorAvatar } from '@/components/tutor/TutorAvatar';
import { STUDY_EXECUTION_LABELS, type StudyExecutionPhase } from './studyExecutionPresentation';
import './studyExecutionMotion.css';

/** Decorative motion loops inside the current real state; it never advances a task. */
export function StudyExecutionStatus({
  phase,
  paused = false,
  label,
  detail,
  pages,
  example = false,
}: {
  phase: StudyExecutionPhase;
  paused?: boolean;
  label?: string;
  detail?: string;
  pages?: { read: number; total: number };
  example?: boolean;
}) {
  const Icon =
    phase === 'knowledge'
      ? Lightbulb
      : phase === 'practice'
        ? ListChecks
        : phase === 'notes'
          ? NotebookPen
          : FileText;
  const count =
    pages &&
    Number.isInteger(pages.read) &&
    Number.isInteger(pages.total) &&
    pages.total > 0 &&
    pages.read >= 0 &&
    pages.read <= pages.total;
  return (
    <div
      className="study-execution my-3 flex min-w-0 items-center gap-3 rounded-2xl border border-hairline bg-surface px-3 py-3"
      data-phase={phase}
      data-active={!paused}
      data-example={example}
    >
      <TutorAvatar
        expression={
          paused
            ? 'reassuring'
            : phase === 'upload' || phase === 'queued'
              ? 'listening'
              : 'thinking'
        }
        animated={!paused && phase !== 'queued'}
        size="small"
      />
      <div className="min-w-0 flex-1" role="status" aria-live="polite" aria-atomic="true">
        <p className="text-[11px] font-bold text-slate2">
          {example
            ? 'Example · pre-prepared'
            : phase === 'upload'
              ? 'Preparing your material'
              : 'Tuan Tuan · AI study helper'}
        </p>
        <p className="mt-0.5 text-sm font-extrabold text-ink">
          {paused ? 'Taking a pause' : (label ?? STUDY_EXECUTION_LABELS[phase])}
        </p>
        {pages && count && phase === 'reading' && (
          <p className="mt-0.5 text-xs font-medium text-slate2">
            {pages.read} of {pages.total} pages read
          </p>
        )}
        {detail && <p className="mt-0.5 text-xs leading-relaxed text-ink-soft">{detail}</p>}
      </div>
      <div
        className="study-execution-scene relative h-12 w-12 shrink-0 text-brand-sky"
        aria-hidden="true"
      >
        <span className="study-execution-paper absolute inset-1 rounded-lg border border-hairline bg-canvas-pure" />
        <Icon className="study-execution-icon absolute inset-2 h-8 w-8" strokeWidth={1.6} />
        {phase === 'reading' && (
          <span className="study-execution-scan absolute left-2 right-2 top-2 h-0.5 rounded-full bg-brand-sky" />
        )}
        {phase === 'notes' && (
          <span className="study-execution-ink absolute bottom-2 left-2 right-2 h-1 origin-left rounded-full bg-brand-mint" />
        )}
        {phase === 'knowledge' && (
          <span className="study-execution-idea absolute right-0 top-0 h-2 w-2 rounded-full bg-brand-sunshine" />
        )}
      </div>
    </div>
  );
}
